/**
 * `signup-auto-alert` — turn the sign-in moment into a job alert.
 *
 * Evidence (scripts/research/anon-vs-authed-2026-10/README.md, 2026-10-26):
 * the session in which a seeker signs in is the deepest one in the product
 * (median 10 pageviews, 509 s), yet within-person pageview days only go
 * 1.01 → 1.30 in the week after, and the standalone alert capture converts
 * 0.6% (7 of 1,204). This experiment offers, right after sign-in, a one-tap
 * alert prefilled from what the visitor browsed while anonymous.
 *
 * Plumbing: `/signup` and `/login` read the anonymous category cookie
 * (`ld_pref_v1`, host-only on the apex) server-side and append
 * `?signup_alert=<slug|all>` to the post-auth callback. The URL carries it
 * across the apex → app.lokerdollar.com redirect, where the cookie is not
 * readable. `SignupAutoAlert` reads it from the browser URL only, so no page
 * HTML varies on it.
 *
 * The PostHog flag is the sole switch (ENABLED = offer shown). It is
 * evaluated server-side per user so a 20% holdout splits arms, and so
 * `PREVIEW_FORCE_FLAGS` can force it in a preview.
 */

import type { SavedSearchFilters } from "@/lib/db/queries/saved-searches";

export const SIGNUP_AUTO_ALERT_FLAG = "signup-auto-alert";

/** Query param carried on the post-auth callback. */
export const SIGNUP_ALERT_PARAM = "signup_alert";

/** Value used when the visitor browsed no category: an all-jobs alert. */
export const SIGNUP_ALERT_ALL = "all";

/** An account older than this is not a new sign-up; the offer never shows. */
export const SIGNUP_AUTO_ALERT_MAX_ACCOUNT_AGE_MS = 24 * 60 * 60 * 1000;

const SLUG_PATTERN = /^[a-z][a-z0-9-]{0,40}$/;

/** Param value from the newest anonymous category slug (first in the list). */
export function signupAlertParamFromCategories(
	categorySlugs: readonly string[],
): string {
	const newest = categorySlugs.find((s) => SLUG_PATTERN.test(s));
	return newest ?? SIGNUP_ALERT_ALL;
}

/**
 * Appends the param to a same-origin callback path. An existing value wins
 * (a refresh of /signup must not stack params). Returns the input unchanged
 * when it does not parse as a path.
 */
export function appendSignupAlertParam(
	callback: string,
	value: string,
): string {
	if (!callback.startsWith("/")) return callback;
	try {
		const url = new URL(callback, "https://example.invalid");
		if (url.searchParams.has(SIGNUP_ALERT_PARAM)) return callback;
		url.searchParams.set(SIGNUP_ALERT_PARAM, value);
		return `${url.pathname}${url.search}${url.hash}`;
	} catch {
		return callback;
	}
}

/**
 * Param value → saved-search filters, or null when the value is not one this
 * module could have written (never trust the URL).
 */
export function filtersFromSignupAlertParam(
	raw: string | null | undefined,
): SavedSearchFilters | null {
	if (!raw) return null;
	if (raw === SIGNUP_ALERT_ALL) return {};
	return SLUG_PATTERN.test(raw) ? { category: raw } : null;
}

/** Human label for a category slug ("data-entry" → "data entry"). */
export function signupAlertRoleLabel(raw: string): string | null {
	if (raw === SIGNUP_ALERT_ALL || !SLUG_PATTERN.test(raw)) return null;
	return raw.replace(/-/g, " ");
}

/** True when the account was created within the new-sign-up window. */
export function isNewAccount(
	createdAt: Date | string | number | null | undefined,
	now: number = Date.now(),
): boolean {
	if (createdAt === null || createdAt === undefined) return false;
	const ms = new Date(createdAt).getTime();
	if (Number.isNaN(ms)) return false;
	return now - ms >= 0 && now - ms <= SIGNUP_AUTO_ALERT_MAX_ACCOUNT_AGE_MS;
}
